'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Feature {
  id: number;
  title: string;
  description: string;
  icon: string;
  image: string;
  alt: string;
}

const PlatformPreview = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const features: Feature[] = [
  {
    id: 1,
    title: 'Real-Time Project Tracking',
    description: 'Monitor manufacturing progress with live updates, quality checkpoints, and milestone tracking throughout your project lifecycle.',
    icon: 'ChartBarIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_122daf91b-1764676753294.png",
    alt: 'Digital dashboard showing real-time manufacturing metrics with progress bars and quality indicators on multiple monitors'
  },
  {
    id: 2,
    title: 'Quality Documentation Portal',
    description: 'Access comprehensive quality reports, certifications, test results, and compliance documentation instantly from your secure portal.',
    icon: 'DocumentTextIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1892891a3-1783233116587.png",
    alt: 'Digital quality documentation system displaying certification reports and test results on tablet device'
  },
  {
    id: 3,
    title: 'Collaborative Engineering',
    description: 'Work directly with our engineering team through integrated communication tools, design reviews, and technical consultations.',
    icon: 'UserGroupIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19f806708-1767348932387.png",
    alt: 'Engineering team collaborating around large display showing technical CAD drawings and project specifications'
  }];


  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isHydrated, features.length]);

  const handleFeatureChange = (index: number) => {
    if (!isHydrated) return;
    setActiveFeature(index);
  };

  return (
    <section className="relative py-24 bg-gradient-to-b from-primary to-background overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(0, 212, 255, 0.3) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="relative mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 rounded-full border border-accent/30 mb-6">
            <Icon name="CommandLineIcon" size={20} className="text-accent" />
            <span className="font-cta text-sm text-accent">Platform Command Center</span>
          </div>
          <h2 className="font-headline text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Your Manufacturing Mission Control
          </h2>
          <p className="font-body text-lg text-technical-gray">
            Comprehensive digital platform for seamless project management, real-time tracking, and collaborative engineering excellence.
          </p>
        </div>

        {/* Platform Preview */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Feature List */}
          <div className="space-y-6">
            {features.map((feature, index) =>
            <button
              key={feature.id}
              onClick={() => handleFeatureChange(index)}
              disabled={!isHydrated}
              className={`w-full text-left p-6 rounded-xl border transition-all duration-300 ${
              activeFeature === index ?
              'bg-card border-accent shadow-glow' :
              'bg-card/50 border-border hover:border-accent/50'}`
              }>

                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                activeFeature === index ?
                'bg-accent/20' : 'bg-muted'}`
                }>
                    <Icon
                    name={feature.icon as any}
                    size={24}
                    className={activeFeature === index ? 'text-accent' : 'text-technical-gray'} />

                  </div>
                  <div className="flex-1">
                    <h3 className={`font-headline text-xl font-bold mb-2 transition-colors duration-300 ${
                  activeFeature === index ? 'text-accent' : 'text-foreground'}`
                  }>
                      {feature.title}
                    </h3>
                    <p className="font-body text-sm text-technical-gray leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                  <Icon
                  name="ChevronRightIcon"
                  size={20}
                  className={`flex-shrink-0 transition-all duration-300 ${
                  activeFeature === index ?
                  'text-accent opacity-100' : 'text-technical-gray opacity-0'}`
                  } />

                </div>
              </button>
            )}

            {/* CTA Button */}
            <Link
              href="/client-portal"
              className="inline-flex items-center space-x-3 px-8 py-4 bg-mission-orange text-primary font-cta text-base font-medium rounded-lg hover:shadow-glow transition-all duration-300 hover:-translate-y-1">

              <span>ACCESS PLATFORM</span>
              <Icon name="ArrowRightIcon" size={20} />
            </Link>
          </div>

          {/* Feature Preview */}
          <div className="relative">
            <div className="relative bg-card border border-accent/30 rounded-2xl overflow-hidden shadow-aerospace">
              {/* Browser Chrome */}
              <div className="bg-muted border-b border-border px-4 py-3 flex items-center space-x-2">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 rounded-full bg-error"></div>
                  <div className="w-3 h-3 rounded-full bg-warning"></div>
                  <div className="w-3 h-3 rounded-full bg-success"></div>
                </div>
                <div className="flex-1 flex items-center justify-center">
                  <div className="px-4 py-1 bg-background rounded-md border border-border">
                    <span className="font-cta text-xs text-technical-gray">platform.techaero.com</span>
                  </div>
                </div>
              </div>

              {/* Feature Image */}
              <div className="relative aspect-[4/3]">
                <AppImage
                  src={features[activeFeature].image}
                  alt={features[activeFeature].alt}
                  className="w-full h-full object-cover" />

                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent"></div>
                
                {/* Feature Label */}
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-card/90 backdrop-blur-sm border border-accent/30 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-accent/20 rounded-lg flex items-center justify-center">
                        <Icon name={features[activeFeature].icon as any} size={20} className="text-accent" />
                      </div>
                      <div>
                        <p className="font-headline text-sm font-bold text-foreground">
                          {features[activeFeature].title}
                        </p>
                        <p className="font-body text-xs text-technical-gray">
                          Active Feature
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Stats */}
            <div className="absolute -top-6 -right-6 bg-card border border-accent rounded-xl p-4 shadow-glow">
              <div className="flex items-center space-x-3">
                <Icon name="BoltIcon" size={24} className="text-accent" />
                <div>
                  <p className="font-headline text-xl font-bold text-foreground">24/7</p>
                  <p className="font-body text-xs text-technical-gray">Platform Access</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default PlatformPreview;